import { SDK } from '@symbo.ls/sdk'
import { deepDestringifyFunctions } from '@symbo.ls/utils'
import { apiUrl as channelApiUrl } from '@symbo.ls/channels'
import {
  hydrateAuthFromCookie,
  hydrateAuthFromIframeBridge,
  installAuthSync,
  switchWorkspace as bridgeSwitchWorkspace
} from '../governanceBridge.js'
import { workspaceApiBase } from './workspaceApiBase.js'
import { governanceSessionAccessToken } from './supabaseConfig.js'

// SDK token provider for /workspace/* surfaces.
//
// The wrapper at next.api.symbols.app/workspace (and the local express
// bridge) authenticates by reading `Authorization: Bearer <jwt>` and
// extracting the `workspace_id` / `active_workspace_id` claim. The
// Symbols SDK's own token (HS256, signed with SYMBOLS_SERVER secret)
// does NOT carry that claim — only the governance Supabase JWT does.
//
// Without this, `WorkspaceProjectService._resolveAuthHeader` falls back
// to `tokenManager.getAuthHeader()` which sends the SDK token, and every
// /workspace/* call 401s with "missing workspace_id claim" or "invalid
// signature" (depending on which secret the wrapper checks against).
//
// Returns null when no governance JWT is in localStorage so the SDK
// service skips the Authorization header — the wrapper will reply 401
// with a clear "missing bearer token" message and bootShell's auth
// diagnostic surfaces the cause.
const _workspaceTokenProvider = async () => {
  if (typeof window === 'undefined') return null
  return governanceSessionAccessToken() || null
}

// ── SDK instances per apiUrl ────────────────────────────────────────────────
// The SDK (see sdk/src/config/environment.js) picks its own apiUrl based on
// `SYMBOLS_APP_ENVIRONMENT`; we only override with an explicit `apiUrl` when
// the caller opts into `--local` or `--next` so we don't re-hardcode the
// production URL here. Channel URLs come from @symbo.ls/channels — single
// source of truth across sdk, smbls, server, editor, workspace, platform.

const _sdkByKey = new Map()
const _initPromiseByKey = new Map()

export function getSDK ({ useLocal = false, useNext = true } = {}) {
  // Default: pin to next.api.symbols.app because the SDK's own environment
  // detection would pick `dev.api.symbols.app` for a `*.localhost` host and
  // that domain doesn't resolve.
  const apiUrl = resolveApiUrl({ useLocal, useNext })
  const cacheKey = apiUrl || 'default'
  if (!_sdkByKey.has(cacheKey)) {
    hydrateAuthFromCookie()
    installAuthSync()
    const opts = { debug: false }
    if (apiUrl) opts.apiUrl = apiUrl
    const sdk = new SDK(opts)
    // Pass the governance token provider into SDK init so
    // WorkspaceProjectService._resolveAuthHeader picks the federated
    // governance JWT (which carries workspace_id) over the SDK's own
    // token (which doesn't).
    //
    // `federationSwitchWorkspace` makes `sdk.switchWorkspace(wsId)` the
    // single entry point for org/workspace switching — the SDK calls
    // sdk-bridge under the hood (governance Supabase Edge Function +
    // session refresh). Frontend never imports sdk-bridge for this.
    const initPromise = sdk.initialize({
      workspaceProjectTokenProvider: _workspaceTokenProvider,
      federationSwitchWorkspace: bridgeSwitchWorkspace
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[sdk] initialize failed:', err?.message || err)
    })
    _initPromiseByKey.set(cacheKey, initPromise)
    _sdkByKey.set(cacheKey, wrapWithInitAwait(sdk, initPromise))
  }
  return _sdkByKey.get(cacheKey)
}

function resolveApiUrl ({ useLocal, useNext } = {}) {
  if (useLocal) return channelApiUrl('local')
  // Single source of truth — `workspaceApiBase` resolves through
  // `@symbo.ls/channels` after honoring runtime + localStorage
  // overrides + the .localhost dev default (auto → `localNext` channel).
  // The SDK and every other workspace API consumer (Supabase
  // passthrough, agentMessages, dashboard slug check) share this base
  // so flipping one knob (`localStorage.symbols_api_channel='localNext'`,
  // or `localStorage.symbols_workspace_api='https://...'`) routes every
  // /workspace/* and /core/* call at once.
  if (typeof window !== 'undefined') return workspaceApiBase()
  if (useNext !== false) return channelApiUrl('next')
  return undefined
}

// Await the SDK initialization promise for the given opts without calling
// initialize() a second time (which would wipe auth state on the real SDK).
export async function awaitSDKInit (opts = {}) {
  const cacheKey = resolveApiUrl(opts) || 'default'
  getSDK(opts)
  return _initPromiseByKey.get(cacheKey)
}

// Wrap the SDK so every method call awaits initialization first. Property
// reads for non-function values (e.g. sdk.environment, sdk.rootBus) pass
// through synchronously.
// These methods read local state synchronously and must not be wrapped —
// callers use them as guards (!!sdk.getAuthToken()) before async work.
const _SYNC_PASS = new Set(['getAuthToken', 'isReady', 'getToken', 'logout'])

function wrapWithInitAwait (sdk, initPromise) {
  return new Proxy(sdk, {
    get (target, prop) {
      const value = target[prop]
      if (typeof value !== 'function') return value
      if (_SYNC_PASS.has(prop)) return value.bind(target)
      return (...args) => initPromise.then(() => value.apply(target, args))
    },
  })
}

// ── Auth ────────────────────────────────────────────────────────────────────

export async function getMe (opts) {
  const result = await getSDK(opts).getMe()
  return result?.data || null
}

export async function login (email, password, opts) {
  return getSDK(opts).login(email, password)
}

export async function logout (opts) {
  return getSDK(opts).logout()
}

export function isAuthenticated (opts) {
  return getSDK(opts).isReady()
}

// sessionStorage-backed caches so every tab/reload doesn't re-hit
// `/me` and `/organizations` — the API rate-limits hard when multiple
// tabs open at once. Each cache also dedupes concurrent callers within
// a single tab via a shared in-flight promise.
const USER_PROFILE_TTL_MS = 5 * 60_000
const USER_PROFILE_KEY = '__canvas_user_profile_v1'
let USER_PROFILE_PROMISE = null

const USER_ORGS_TTL_MS = 5 * 60_000
const USER_ORGS_KEY = '__canvas_user_orgs_v1'
let USER_ORGS_PROMISE = null

function readCache (key) {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at) return null
    return entry
  } catch { return null }
}

function writeCache (key, data) {
  try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data })) } catch {}
}

export async function getUserProfileCached (opts) {
  const entry = readCache(USER_PROFILE_KEY)
  if (entry && Date.now() - entry.at < USER_PROFILE_TTL_MS) return entry.data
  if (USER_PROFILE_PROMISE) return USER_PROFILE_PROMISE
  const sdk = getSDK(opts)
  USER_PROFILE_PROMISE = Promise.resolve(sdk.getUserProfile())
    .then(r => {
      const data = r?.data || r || null
      if (data) writeCache(USER_PROFILE_KEY, data)
      return data
    })
    .catch(err => {
      // Fall back to stale cache on 429/network errors — stale user data
      // beats a broken dashboard. Only re-throw when we have nothing.
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => { USER_PROFILE_PROMISE = null })
  return USER_PROFILE_PROMISE
}

export async function listOrganizationsCached (opts) {
  const entry = readCache(USER_ORGS_KEY)
  if (entry && Date.now() - entry.at < USER_ORGS_TTL_MS) return entry.data
  if (USER_ORGS_PROMISE) return USER_ORGS_PROMISE
  const sdk = getSDK(opts)
  USER_ORGS_PROMISE = Promise.resolve(sdk.listOrganizations())
    .then(r => {
      const data = Array.isArray(r) ? r : (r?.data || r?.organizations || [])
      writeCache(USER_ORGS_KEY, data)
      return data
    })
    .catch(err => {
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => { USER_ORGS_PROMISE = null })
  return USER_ORGS_PROMISE
}

export function invalidateUserDataCache () {
  try {
    sessionStorage.removeItem(USER_PROFILE_KEY)
    sessionStorage.removeItem(USER_ORGS_KEY)
  } catch {}
  USER_PROFILE_PROMISE = null
  USER_ORGS_PROMISE = null
}

// ── Project ─────────────────────────────────────────────────────────────────

// Anonymous fetch for public projects — preview subdomains use this so
// project data loads without an auth cookie. Returns null when the project
// isn't public or the env isn't configured; caller can fall back.
export async function fetchPublicProjectFromSDK (owner, key, { envKey, ...sdkOpts } = {}) {
  if (!envKey) return null
  const sdk = getSDK(sdkOpts)
  const projectKey = `${owner}/${key}`
  const result = await sdk.getPublicProjectDataByKey(projectKey, { envKey })
  const data = result?.data || result
  if (!data) return null
  return typeof data === 'string' ? data : deepDestringifyFunctions(data)
}

// Anonymous probe — returns 'public' | 'private' | 'password-protected' | null.
// Used by the preview surface AFTER the env-fallback chain has exhausted, to
// decide between "genuine not-published" vs "redirect to login" vs "show
// password card".
export async function fetchPublicProjectVisibilityFromSDK (owner, key, sdkOpts = {}) {
  const sdk = getSDK(sdkOpts)
  const projectKey = `${owner}/${key}`
  return sdk.getPublicProjectVisibility(projectKey)
}

// Anonymous unlock for password-protected projects. `password` is the
// SHA-256 hex digest of the user-typed value (matches the hash stored on
// project.projectPassword). Returns project data on match, null on
// invalid password — caller re-prompts.
export async function unlockPublicProjectFromSDK (owner, key, { envKey, password, ...sdkOpts } = {}) {
  if (!envKey || !password) return null
  const sdk = getSDK(sdkOpts)
  const projectKey = `${owner}/${key}`
  const result = await sdk.unlockPublicProjectDataByKey(projectKey, { envKey, password })
  const data = result?.data || result
  if (!data) return null
  return typeof data === 'string' ? data : deepDestringifyFunctions(data)
}

export async function fetchProjectFromSDK (owner, key, options = {}) {
  const sdk = getSDK(options)
  const projectKey = `${owner}/${key}`
  const result = await sdk.getProjectDataByKey(projectKey, {
    branch: options.branch || 'main',
    version: options.version || 'latest'
  })
  const data = result?.data || result
  if (!data) return null
  return typeof data === 'string' ? data : deepDestringifyFunctions(data)
}

// Look up an environment's publish config ({mode, branch, version}) for a
// project by key and distill it into the {branch, version} pair that
// getProjectDataByKey accepts. Returns null when the project has no
// matching env configured — callers then fall back to their own default.
export async function resolveEnvPublishConfig (projectKey, envKey, sdkOpts = {}) {
  if (!projectKey || !envKey) return null
  const sdk = getSDK(sdkOpts)
  const project = await sdk.getProjectByKey(projectKey)
  const projectId = project?._id || project?.id
  if (!projectId) return null
  const envs = await sdk.listEnvironments(projectId)
  const cfg = envs?.environments?.[envKey]
  if (!cfg || cfg.enabled === false) return null
  const branch = cfg.branch || 'main'
  const version = cfg.mode === 'version' && cfg.version ? cfg.version : 'latest'
  return { branch, version }
}

export async function listUserProjects (opts) {
  const result = await getSDK(opts).getUserProjects()
  return result?.data || []
}

// ── URL helpers ─────────────────────────────────────────────────────────────

export function getProjectParamsFromUrl () {
  const params = new URLSearchParams(window.location.search)
  return {
    owner: params.get('owner'),
    key: params.get('key'),
    env: params.get('env') || 'development',
    branch: params.get('branch') || 'main'
  }
}
